import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FileText, Download, Eye, ArrowLeftRight, DollarSign, Calendar, TrendingUp, BarChart2, Users, Building2, Search } from "lucide-react";
import { bondsAPI, formatDate, allocationRequestsAPI } from "../../services/api";
import { createResponseSelector } from "../../utils/apiHelpers";
import LoadingSpinner from "../../components/common/LoadingSpinner";

const STATUS_CONFIG = {
    Draft: { className: "bg-gray-100 text-gray-800", icon: "📝" },
    "Pending Review": { className: "bg-yellow-100 text-yellow-800", icon: "⏳" },
    Approved: { className: "bg-green-100 text-green-800", icon: "✅" },
    Listed: { className: "bg-green-100 text-green-800", icon: "🏛️" },
    Active: { className: "bg-green-100 text-green-800", icon: "✅" },
    Matured: { className: "bg-blue-100 text-blue-800", icon: "🏁" },
};

const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || { className: "bg-gray-100 text-gray-800", icon: "" };
    return (
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${config.className}`}>
            {config.icon && <span className="mr-1">{config.icon}</span>}
            {status}
        </span>
    );
};

const CustodianDocumentManagement = () => {
    const navigate = useNavigate();
    const [selectedSection, setSelectedSection] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 10;

    const documentSections = [
        {
            id: "token-mint-transfer",
            title: "Token Mint and Transfer Log",
            icon: ArrowLeftRight,
            description: "Complete log of token minting and transfer activities"
        },
        {
            id: "profit-distribution",
            title: "Profit Distribution/Coupon Payment Report",
            icon: DollarSign,
            description: "Reports on profit distributions and coupon payments"
        },
        {
            id: "redemption-payment",
            title: "Redemption Payment Report",
            icon: Calendar,
            description: "Track and manage redemption payment records"
        },
        {
            id: "daily-accrual-profit",
            title: "Daily Accrual Profit Report",
            icon: TrendingUp,
            description: "Daily profit accrual calculations and reports"
        },
        {
            id: "daily-position",
            title: "Daily Position Report",
            icon: BarChart2,
            description: "Daily position and holdings status"
        },
        {
            id: "transaction-file",
            title: "Transaction File",
            icon: FileText,
            description: "Complete transaction history and records"
        },
        {
            id: "sub-investor-status",
            title: "Sub-Investor Status Report",
            icon: Users,
            description: "Status and activity reports for sub-investors"
        }
    ];

    // Debounce search term
    React.useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    // Reset to first page when status filter changes
    React.useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter]);

    // Fetch bonds with server-side pagination and filtering
    const { data: bondsResponse, isLoading: bondsLoading } = useQuery({
        queryKey: ['custodianBonds', debouncedSearchTerm, statusFilter, currentPage],
        queryFn: async () => {
            const params = {
                page: currentPage,
                limit: PAGE_SIZE,
            };
            if (debouncedSearchTerm) {
                params.search = debouncedSearchTerm;
            }
            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }
            const response = await allocationRequestsAPI.getBondsByCustodian(params);
            return response;
        },
        select: data => {
            const responseData = data.data;
            if (responseData && responseData.success && responseData.data) {
                return responseData.data;
            }
            return { bonds: [], total: 0, page: 1, totalPages: 1 };
        },
    });

    const bonds = bondsResponse?.bonds || [];
    const totalBonds = bondsResponse?.total || 0;
    const totalPages = bondsResponse?.totalPages || 1;

    const DocumentCard = ({ section }) => {
        const Icon = section.icon;

        return (
            <div className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-all">
                <div className="flex items-start justify-between mb-3">
                    <Icon className="w-6 h-6 text-gray-600" />
                    <div className="flex gap-2">
                        <button className="p-1 rounded hover:bg-white/50" title="View">
                            <Eye className="w-4 h-4 text-gray-600" />
                        </button>
                        <button className="p-1 rounded hover:bg-white/50" title="Download">
                            <Download className="w-4 h-4 text-gray-600" />
                        </button>
                    </div>
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">{section.title}</h3>
                <p className="text-xs text-gray-700">{section.description}</p>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="bg-white shadow">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">
                                Document Management
                            </h1>
                            <p className="text-sm text-gray-500">
                                Manage bond-related documents and reports
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bond Documents Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Bond Documents</h2>
                </div>

                {/* Filters */}
                <div className="mb-6 flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search bonds..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-4 py-2 pr-8 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 min-w-[140px] whitespace-nowrap bg-white"
                    >
                        <option value="all">All Status</option>
                        <option value="Active">Active</option>
                        <option value="Listed">Listed</option>
                        <option value="Approved">Approved</option>
                        <option value="Matured">Matured</option>
                    </select>
                </div>

                {/* Bonds Table */}
                {bondsLoading ? (
                    <LoadingSpinner size="lg" text="Loading bonds..." />
                ) : (
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        {bonds.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                                <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                                <p>{searchTerm || statusFilter !== "all" ? 'No bonds match your filters' : 'No bonds found'}</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Bond Name
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Issue Date
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Maturity Date
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {bonds.map((bond) => (
                                            <tr key={bond._id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {bond.bondName}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-500">
                                                        {bond.issueDate ? formatDate(bond.issueDate) : "N/A"}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-500">
                                                        {bond.maturityDate ? formatDate(bond.maturityDate) : "N/A"}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {bond.status && getStatusBadge(bond.status)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    <button
                                                        onClick={() => navigate(`/custodian/bonds/${bond._id}/documents`)}
                                                        className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                                                        title="View Documents"
                                                    >
                                                        <FileText className="h-4 w-4" />
                                                        View
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination */}
                        {bonds.length > 0 && totalPages > 1 && (
                            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                                <div className="flex-1 flex justify-between sm:hidden">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                    </button>
                                </div>
                                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                    <div>
                                        <p className="text-sm text-gray-700">
                                            Showing {' '}
                                            <span className="font-medium">{(currentPage - 1) * PAGE_SIZE + 1}</span>
                                            {' '} - {' '}
                                            <span className="font-medium">
                                                {Math.min(currentPage * PAGE_SIZE, totalBonds)}
                                            </span>
                                            {' '} of {' '}
                                            <span className="font-medium">{totalBonds}</span>
                                            {' '} results
                                        </p>
                                    </div>
                                    <div>
                                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                            <button
                                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                disabled={currentPage === 1}
                                                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <span className="sr-only">Previous</span>
                                                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                            {[...Array(totalPages)].map((_, idx) => {
                                                const pageNum = idx + 1;
                                                // Show first page, last page, current page, and pages around current
                                                const showPage = pageNum === 1 ||
                                                    pageNum === totalPages ||
                                                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1);

                                                if (!showPage) {
                                                    // Show ellipsis
                                                    if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                                                        return (
                                                            <span
                                                                key={pageNum}
                                                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                                                            >
                                                                ...
                                                            </span>
                                                        );
                                                    }
                                                    return null;
                                                }

                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => setCurrentPage(pageNum)}
                                                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNum
                                                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                            }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                            <button
                                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                disabled={currentPage === totalPages}
                                                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <span className="sr-only">Next</span>
                                                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                        </nav>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustodianDocumentManagement;
